// Copyright (c) 2025 WiseTime. All rights reserved.

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import * as Actions from "./actions"
import type { RootState } from "../../app/store"
import {
  NarratePreferences,
  TrainingGroup,
  TrainingNarrative,
} from "../../generated/wisetime/user/assistant/v4/preferences_pb"

type TrainingNarrativesCache = {
  [id: string]: TrainingNarrative,
}

export type State = {
  defaultNarratePreferences: NarratePreferences | undefined,
  savedNarratePreferences: NarratePreferences | undefined,
  previewNarratePreferences: NarratePreferences | undefined,
  trainingNarrativesCache: TrainingNarrativesCache,
  trainingGroups: TrainingGroup[],
  replacedGroupIds: string[],
}

export const initialState: State = {
  defaultNarratePreferences: undefined,
  savedNarratePreferences: undefined,
  previewNarratePreferences: undefined,
  trainingNarrativesCache: {},
  trainingGroups: [],
  replacedGroupIds: [],
}

export const slice = createSlice({
  name: "narratePreferences",
  initialState,
  reducers: {
    setDefaultNarratePreferences: (state, action: PayloadAction<NarratePreferences>): void => {
      state.defaultNarratePreferences = action.payload
    },
    setSavedNarratePreferences: (state, action: PayloadAction<NarratePreferences | undefined>): void => {
      state.savedNarratePreferences = action.payload
    },
    setPreviewNarratePreferences: (state, action: PayloadAction<NarratePreferences | undefined>): void => {
      state.previewNarratePreferences = action.payload
    },
    setTrainingGroups: (state, action: PayloadAction<TrainingGroup[]>): void => {
      state.trainingGroups = action.payload
    },
    setTrainingGroup: (state, action: PayloadAction<{
      trainingGroup: TrainingGroup,
      replaceGroupId: string,
    }>): void => {
      const index = state.trainingGroups.findIndex(g => g.id === action.payload.replaceGroupId)
      if (index !== -1) {
        state.trainingGroups[index] = action.payload.trainingGroup
      }
    },
    setTrainingNarrative: (state, action: PayloadAction<TrainingNarrative>): void => {
      const id = action.payload.trainingGroup?.id
      if (!id) {
        return
      }
      state.trainingNarrativesCache[id] = action.payload
    },
    resetReplacedGroupIds: (state) => {
      state.replacedGroupIds = initialState.replacedGroupIds
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        Actions.listTrainingGroups,
        (state, action: PayloadAction<Actions.ListTrainingGroupsPayload>): void => {
          const index = state.replacedGroupIds.findIndex(id => id === action.payload.replaceGroupId)
          if (action.payload.replaceGroupId && index === -1) {
            state.replacedGroupIds.push(action.payload.replaceGroupId)
          }
        },
      )
      .addCase(
        Actions.getTrainingNarrative,
        (state, action): void => {
          const existingItem = state.trainingNarrativesCache[action.payload.trainingGroup?.id ?? ""]
          if (existingItem) {
            existingItem.narrative = ""
          }
        },
      )
  },
})

export const {
  setDefaultNarratePreferences,
  setSavedNarratePreferences,
  setPreviewNarratePreferences,
  setTrainingGroups,
  setTrainingGroup,
  setTrainingNarrative,
  resetReplacedGroupIds,
} = slice.actions

export default slice.reducer

export const selectDefaultNarratePreferences = (state: RootState): NarratePreferences | undefined =>
  state.narratePreferences.defaultNarratePreferences
export const selectSavedNarratePreferences = (state: RootState): NarratePreferences | undefined =>
  state.narratePreferences.savedNarratePreferences
export const selectTrainingNarratives = createSelector(
  (state: RootState) => state.narratePreferences.trainingNarrativesCache,
  (trainingNarrativesCache): TrainingNarrative[] => Object.values(trainingNarrativesCache)
)
export const selectReplacedGroupIds = (state: RootState): string[] =>
  state.narratePreferences.replacedGroupIds
export type TrainingEntity = {
  group: TrainingGroup,
  narrative: string | undefined,
}
export const selectTrainingEntities = createSelector(
  (state: RootState) => state.narratePreferences.trainingGroups,
  (state: RootState) => state.narratePreferences.trainingNarrativesCache,
  (trainingGroups, trainingNarrativesCache): TrainingEntity[] =>
    trainingGroups.map(trainingGroup => ({
      group: trainingGroup,
      narrative: trainingNarrativesCache[trainingGroup.id]?.narrative,
    }))
)
